import { Close, Search } from 'flowbite-react-icons/outline'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import type { ClinicalSummaryCategory } from './clinicalSummaryCategoryStyles'
import type { ClinicalSummaryChipMoveTarget } from './clinicalSummaryChipMoveTargets'

/** Figma S03: the menu sits 9px under the chip that opens it. */
const MENU_GAP = 9

/** The end of the menu that takes focus when an arrow key opened it; a new object moves focus again. */
export interface ClinicalSummaryChipMenuFocus {
  edge: 'first' | 'last'
}

interface ClinicalSummaryChipMenuProps {
  id: string
  /** The chip that opens the menu. */
  labelledBy: string
  focus: ClinicalSummaryChipMenuFocus | null
  moveTargets: readonly ClinicalSummaryChipMoveTarget[]
  disabled?: boolean
  /** `fromKeyboard`: Enter or Space chose the item, so focus has to go somewhere once the menu closes. */
  onMove: (category: ClinicalSummaryCategory, fromKeyboard: boolean) => void
  /** Left out when the term has no phrase in the summary. */
  onShowInSummary?: (fromKeyboard: boolean) => void
  onRemove: (fromKeyboard: boolean) => void
}

interface MenuAction {
  label: string
  icon: ReactNode
  tone: string
  choose: (fromKeyboard: boolean) => void
}

// helix-exception: the move glyph is not in flowbite-react-icons 1.3.1 outline, so it stays an <img> (F-13)
const MOVE_ICON = <img src="/icons/clinical-summary-move.svg" alt="" aria-hidden className="size-4 shrink-0" />
const SHOW_ICON = <Search className="size-4 shrink-0" aria-hidden />
const REMOVE_ICON = <Close className="size-4 shrink-0" aria-hidden />

const ITEM_CLASS =
  'flex w-full cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-sm p-2 text-left text-sm font-medium leading-[14px] transition-colors duration-150 hover:bg-background-tertiary dark:hover:bg-gray-800 focus-visible:bg-background-tertiary dark:focus-visible:bg-gray-800 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none'

/** A click Enter or Space produced carries no pointer detail. */
const fromKeyboard = (event: MouseEvent) => event.detail === 0

/**
 * S03's chip menu: a 235px overlay 9px under the chip, its left edge on the chip's. Chrome sets the
 * longest label a few pixels wider than Figma; it runs into the item's end padding rather than widen the menu.
 */
export function ClinicalSummaryChipMenu({
  id,
  labelledBy,
  focus,
  moveTargets,
  disabled = false,
  onMove,
  onShowInSummary,
  onRemove,
}: ClinicalSummaryChipMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const items = () => [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])]

  /**
   * The menu hangs below its chip, and the modal's body scrolls: an absolutely positioned overlay
   * is clipped at that scroller's edge however high its z-index, which hid the menu entirely.
   * `fixed` is laid out against the viewport, so it escapes the clip while staying in place in the
   * DOM — no portal, so focus, labelling and the outside-click check all keep working.
   */
  const [viewportPosition, setViewportPosition] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    const chip = document.getElementById(labelledBy)
    if (!chip) return
    const place = () => {
      const box = chip.getBoundingClientRect()
      setViewportPosition({ top: box.bottom + MENU_GAP, left: box.left })
    }
    place()
    // The chip moves under the menu when the modal body scrolls or the window resizes.
    const scroller = chip.closest('.overflow-y-auto')
    scroller?.addEventListener('scroll', place, { passive: true })
    window.addEventListener('resize', place)
    return () => {
      scroller?.removeEventListener('scroll', place)
      window.removeEventListener('resize', place)
    }
  }, [labelledBy])

  useEffect(() => {
    if (!focus) return
    const list = items()
    ;(focus.edge === 'first' ? list[0] : list[list.length - 1])?.focus()
  }, [focus])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = items()
    const current = list.indexOf(document.activeElement as HTMLButtonElement)
    const target =
      event.key === 'ArrowDown'
        ? current + 1
        : event.key === 'ArrowUp'
          ? current - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? list.length - 1
              : null
    if (target === null || list.length === 0) return
    event.preventDefault()
    list[(target + list.length) % list.length]?.focus()
  }

  const actions: MenuAction[] = [
    ...moveTargets.map((target) => ({
      label: target.label,
      icon: MOVE_ICON,
      tone: 'text-text-body dark:text-gray-400',
      choose: (keyboard: boolean) => onMove(target.category, keyboard),
    })),
    ...(onShowInSummary
      ? [{ label: 'Show in summary', icon: SHOW_ICON, tone: 'text-text-body dark:text-gray-400', choose: onShowInSummary }]
      : []),
  ]
  const remove: MenuAction = { label: 'Remove', icon: REMOVE_ICON, tone: 'text-text-fg-error dark:text-error-100', choose: onRemove }
  const item = (action: MenuAction, itemIndex: number) => (
    <button
      key={action.label}
      type="button"
      role="menuitem"
      tabIndex={itemIndex === activeIndex ? 0 : -1}
      disabled={disabled}
      onFocus={() => setActiveIndex(itemIndex)}
      onClick={(event) => action.choose(fromKeyboard(event))}
      className={`${ITEM_CLASS} ${action.tone}`}
    >
      {action.icon}
      {action.label}
    </button>
  )

  return (
    <div
      ref={menuRef}
      id={id}
      role="menu"
      aria-labelledby={labelledBy}
      onKeyDown={onKeyDown}
      // Until it is measured the menu keeps the old flow position, so it is never drawn in the
      // viewport's corner on the first paint.
      style={viewportPosition ?? undefined}
      className={`z-20 flex w-[235px] flex-col rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-1.5 shadow-xl ${
        viewportPosition ? 'fixed' : 'absolute left-0 top-[calc(100%+9px)]'
      }`}
    >
      {actions.map(item)}
      <div role="separator" className="mx-2 my-3 h-px shrink-0 bg-background-quaternary dark:bg-gray-800" />
      {item(remove, actions.length)}
    </div>
  )
}
